if (!customElements.get('wls-header-content-dropdown')) {
    class WLSHeaderContentDropdown extends HTMLElement {
      constructor() {
        super();
  
        this.classes = {
          active: 'active',
          slideClass: 'js-collection-block-slider',
          prevButton: 'js-swiper-btn-prev',
          nextButton: 'js-swiper-btn-next',
          pagination: 'js-custom-pagination',
          customNavigation: 'js-swiper-navigation',
          collectionBlock: 'wls-header-collection-block',
          dropdownMenuTab: 'js-dropdown-menu-tab',
          dropdownMenuTabContent: 'js-dropdown-menu-tab-content',
          dropdownMenuTabsSlider: 'js-dropdown-menu-tabs-slider',
        };

        this.selectors = {
          collectionBlocks: this.querySelectorAll(`${this.classes.collectionBlock}`),
          dropdownMenuTabs: this.querySelectorAll(`.${this.classes.dropdownMenuTab}`),
          dropdownMenuTabContents: this.querySelectorAll(`.${this.classes.dropdownMenuTabContent}`),
          dropdownMenuTabsSlider: this.querySelector(`.${this.classes.dropdownMenuTabsSlider}`),
        };

        this.isLargeScreen = window.innerWidth > 1023;
        this.swiperInstances = new Map(); // Store all swiper instances
      }

      /**
       * Lifecycle method called when the element is added to the DOM.
       * It initializes Swiper for each collection block individually.
       *
       * @returns {void}
       */
      connectedCallback() {
        // Small delay to ensure DOM is fully ready
        setTimeout(() => {
          this.initializeAllCollectionBlocks();
          this.initializeDropdownMenuTabs();
          this.initializeDropdownMenuTabsSlider();
          this.addResizeListener();
        }, 10);
      }

      /**
       * Adds resize listener to handle window size changes
       */
      addResizeListener() {
        this.resizeHandler = () => {
          const newIsLargeScreen = window.innerWidth > 1023;
          if (newIsLargeScreen !== this.isLargeScreen) {
            this.isLargeScreen = newIsLargeScreen;
            this.handleScreenSizeChange();
          }
        };
        
        window.addEventListener('resize', this.resizeHandler);
      }

      /**
       * Handles screen size changes by reinitializing or destroying swipers
       */
      handleScreenSizeChange() {
        if (this.isLargeScreen) {
          // Reinitialize all collection blocks if switching to large screen
          this.initializeAllCollectionBlocks();
        } else {
          // Destroy all swiper instances if switching to small screen
          this.swiperInstances.forEach((swiper) => {
            if (swiper && typeof swiper.destroy === 'function') {
              swiper.destroy(true, true);
            }
          });
          this.swiperInstances.clear();
        }
      }

      /**
       * Lifecycle method called when the element is removed from the DOM.
       * It cleans up all swiper instances and event listeners.
       *
       * @returns {void}
       */
      disconnectedCallback() {
        // Clean up all swiper instances
        this.swiperInstances.forEach((swiper) => {
          if (swiper && typeof swiper.destroy === 'function') {
            swiper.destroy(true, true);
          }
        });
        this.swiperInstances.clear();

        // Remove resize listener
        if (this.resizeHandler) {
          window.removeEventListener('resize', this.resizeHandler);
        }
      }

      initializeDropdownMenuTabsSlider() {
        const swiper = new Swiper(this.selectors.dropdownMenuTabsSlider, {
          slidesPerView: 'auto',
          spaceBetween: 20,
          breakpoints: {
            990: {
              centerInsufficientSlides: true,
            },
          },
        });
      }

      /**
       * Initializes Swiper for all collection blocks within this component.
       * Each block gets its own Swiper instance with its own navigation.
       * All sliders are initialized immediately, regardless of tab visibility.
       *
       * @returns {void}
       */
      initializeAllCollectionBlocks() {
        if (this.isLargeScreen) {
          this.selectors.collectionBlocks.forEach((block, index) => {
            this.initializeSwiperForBlock(block, index);
          });
        }
      }

      initializeDropdownMenuTabs() {
        this.selectors.dropdownMenuTabs.forEach((tab) => {
          tab.addEventListener('click', (e) => {
            const tabIndex = parseInt(tab.dataset.tabIndex);
            this.switchToTab(tabIndex);
          });
        });
      }

      /**
       * Switches to a specific tab with smooth transition
       * @param {number} tabIndex - The index of the tab to switch to
       */
      switchToTab(tabIndex) {
        // Remove active class from all tabs and content
        this.selectors.dropdownMenuTabs.forEach((tab) => {
          tab.classList.remove(this.classes.active);
        });
        
        this.selectors.dropdownMenuTabContents.forEach((content) => {
          content.classList.remove(this.classes.active);
        });

        // Add active class to selected tab and content
        this.selectors.dropdownMenuTabs[tabIndex].classList.add(this.classes.active);
        this.selectors.dropdownMenuTabContents[tabIndex].classList.add(this.classes.active);
        
        // Update swiper instances when tab changes
        this.updateSwiperInstancesOnTabChange(tabIndex);
      }

      /**
       * Updates swiper instances when tab changes to ensure proper functionality
       * @param {number} activeTabIndex - The index of the active tab
       */
      updateSwiperInstancesOnTabChange(activeTabIndex) {
        // Update all swiper instances to ensure they're properly sized
        this.swiperInstances.forEach((swiper, index) => {
          if (swiper && typeof swiper.update === 'function') {
            // Use requestAnimationFrame for better performance
            requestAnimationFrame(() => {
              try {
                swiper.update();
                swiper.updateSize();
                swiper.updateSlides();
                swiper.updateProgress();
                swiper.updateSlidesClasses();
              } catch (error) {
                console.warn(`Error updating swiper instance ${index}:`, error);
              }
            });
          }
        });
      }

      /**
       * Initializes a Swiper instance for a specific collection block.
       * Each block has its own slider, navigation buttons, and pagination.
       *
       * @param {HTMLElement} block - The collection block element
       * @param {number} index - The index of the block for unique identification
       * @returns {void}
       */
      initializeSwiperForBlock(block, index) {
        // Get elements specific to this block
        const slider = block.querySelector(`.${this.classes.slideClass}`);
        const prevButton = block.querySelector(`.${this.classes.prevButton}`);
        const nextButton = block.querySelector(`.${this.classes.nextButton}`);
        const paginationElement = block.querySelector(`.${this.classes.pagination}`);
        const customNavigation = block.querySelector(`.${this.classes.customNavigation}`);

        if (!slider) {
          console.warn(`Slider not found in collection block ${index}`);
          return;
        }

        // Check if swiper is already initialized
        if (slider.swiper) {
          console.warn(`Swiper already initialized for block ${index}`);
          return;
        }

        try {
          const swiper = new Swiper(slider, {
            spaceBetween: 20,
            slidesPerView: 4,
            loop: true,
            centerInsufficientSlides: true,
            observer: true, // Enable observer for dynamic content changes
            observeParents: true, // Observe parent elements
            watchOverflow: true, // Watch for overflow
            watchSlidesProgress: true, // Watch slides progress
            watchSlidesVisibility: true, // Watch slides visibility
            pagination: {
              el: paginationElement,
              clickable: true,
              renderBullet: (bulletIndex, className) => {
                return `
                      <span class="${className} slider-dot">
                          <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
                              <path d="M4.4346 1.10409C4.46647 1.05886 4.53353 1.05886 4.5654 1.10409L4.91353 1.59827C5.07491 1.82737 5.37701 1.90831 5.63131 1.7906L6.17989 1.53669C6.2301 1.51345 6.28819 1.54698 6.29317 1.6021L6.34756 2.20413C6.37278 2.48322 6.59393 2.70437 6.87302 2.72959L7.47505 2.78398C7.53016 2.78896 7.5637 2.84704 7.54046 2.89726L7.28654 3.44584C7.16884 3.70014 7.24978 4.00224 7.47887 4.16362L7.97306 4.51175C8.01829 4.54361 8.01829 4.61068 7.97306 4.64255L7.47887 4.99067C7.24978 5.15206 7.16884 5.45415 7.28654 5.70846L7.54046 6.25704C7.5637 6.30725 7.53016 6.36534 7.47505 6.37032L6.87302 6.42471C6.59393 6.44993 6.37278 6.67108 6.34756 6.95017L6.29317 7.5522C6.28819 7.60731 6.2301 7.64085 6.17989 7.6176L5.63131 7.36369C5.37701 7.24598 5.07491 7.32693 4.91353 7.55602L4.5654 8.0502C4.53353 8.09544 4.46647 8.09544 4.4346 8.0502L4.08647 7.55602C3.92509 7.32693 3.62299 7.24599 3.36869 7.36369L2.82011 7.6176C2.7699 7.64085 2.71181 7.60731 2.70683 7.5522L2.65244 6.95017C2.62722 6.67108 2.40607 6.44993 2.12698 6.42471L1.52495 6.37032C1.46984 6.36534 1.4363 6.30725 1.45954 6.25704L1.71346 5.70846C1.83116 5.45415 1.75022 5.15206 1.52113 4.99067L1.02694 4.64255C0.981707 4.61068 0.981707 4.54361 1.02694 4.51175L1.52113 4.16362C1.75022 4.00224 1.83116 3.70014 1.71346 3.44584L1.45954 2.89726C1.4363 2.84704 1.46984 2.78896 1.52495 2.78398L2.12698 2.72959C2.40607 2.70437 2.62722 2.48322 2.65244 2.20413L2.70683 1.60209C2.71181 1.54698 2.7699 1.51345 2.82011 1.53669L3.36869 1.7906C3.62299 1.90831 3.92509 1.82737 4.08647 1.59827L4.4346 1.10409Z" fill="#010101" stroke="#010101" stroke-width="0.5"/>
                          </svg>
                      </span>
                  `;
              },
            },
            navigation: {
              prevEl: prevButton,
              nextEl: nextButton,
              enabled: true,
            },
            on: {
              init: () => {
                console.log(`Swiper initialized for block ${index}`);
                // Add class to mark swiper as initialized
                slider.classList.add('swiper-initialized');
              },
              resize: () => {
                console.log(`Swiper resized for block ${index}`);
              }
            }
          });

          // Handle navigation lock state for this specific block
          if (customNavigation) {
            if (!swiper.isLocked) {
              customNavigation.classList.remove('slider-navigation-lock');
            } else {
              customNavigation.classList.add('slider-navigation-lock');
            }
          }

          // Store the swiper instance for future reference
          this.swiperInstances.set(index, swiper);
          block.swiperInstance = swiper;

        } catch (error) {
          console.error(`Error initializing swiper for block ${index}:`, error);
        }
      }
    }
  
    window.customElements.define('wls-header-content-dropdown', WLSHeaderContentDropdown);
  }
  